import { useSaveManagerSettingsMutation } from '@streamster/shared/api/api.mutations';
import { useManagerSettingsQuery } from '@streamster/shared/api/api.portal';
import mixins from '@streamster/shared/app/styles';
import Button from '@streamster/shared/components/Button/Button';
import Loader from '@streamster/shared/components/Loader/Loader';
import { ManagerPermission } from '@streamster/shared/constants/managerPermissions';
import { ToasterContext } from '@streamster/shared/toaster/toaster.provider';
import { useContext, useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Heading from '#/components/Heading/Heading';
import {
  ManagerPermissionGrid,
  PermissionCheckbox,
} from '#/components/ManagerPermissionGrid/ManagerPermissionGrid';

const styles = StyleSheet.create({
  wrapper: {
    marginTop: mixins().indent.i10,
  },
  section: {
    marginBottom: mixins().indent.i8,
  },
  // Same three-column layout as the permission grid.
  columns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  column: {
    flexGrow: 1,
    flexBasis: 200,
    minWidth: 180,
    paddingRight: mixins().indent.i6,
  },
  empty: {
    color: mixins().color.grayMedium,
  },
  actions: {
    flexDirection: 'row',
    marginTop: mixins().indent.i4,
  },
});

/** Splits a list into `count` roughly equal columns, filling down each column. */
const toColumns = <T,>(items: T[], count = 3): T[][] => {
  const perColumn = Math.ceil(items.length / count) || 1;
  return Array.from({ length: count }, (_, i) => items.slice(i * perColumn, (i + 1) * perColumn));
};

/**
 * Manager rights + assigned streamers, shown on the Profile screen when the owner has a
 * manager selected. Owner-only: the server rejects a manager editing their own rights.
 */
export const ManagerSettingsSection = ({ managerId }: { managerId: number }) => {
  const { data: settings, isLoading } = useManagerSettingsQuery(managerId);
  const saveMutation = useSaveManagerSettingsMutation();
  const { success, error } = useContext(ToasterContext);

  const [permissions, setPermissions] = useState<ManagerPermission[]>([]);
  const [streamerIds, setStreamerIds] = useState<number[]>([]);

  useEffect(() => {
    if (settings) {
      setPermissions(settings.permissions as ManagerPermission[]);
      setStreamerIds(settings.streamerIds);
    }
  }, [settings]);

  const togglePermission = (permission: ManagerPermission) =>
    setPermissions(prev =>
      prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission],
    );

  const toggleStreamer = (id: number) =>
    setStreamerIds(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));

  const handleSubmit = () =>
    saveMutation.mutate(
      { managerId, permissions, streamerIds },
      {
        onSuccess: () => success({ message: 'Manager settings saved' }),
        onError: (err: any) => error({ title: 'Could not save', message: err?.message }),
      },
    );

  if (isLoading || !settings) {
    return <Loader size={40} />;
  }

  return (
    <View style={styles.wrapper}>
      <View style={styles.section}>
        <Heading>Access</Heading>
        <ManagerPermissionGrid selected={permissions} onToggle={togglePermission} />
      </View>

      <View style={styles.section}>
        <Heading>Assigned users</Heading>
        {settings.streamers.length === 0 ? (
          <Text style={styles.empty}>No streamers in this business yet.</Text>
        ) : (
          <View style={styles.columns}>
            {toColumns(settings.streamers).map((column, i) => (
              <View key={i} style={styles.column}>
                {column.map(streamer => (
                  <PermissionCheckbox
                    key={streamer.id}
                    label={streamer.login}
                    checked={streamerIds.includes(streamer.id)}
                    onToggle={() => toggleStreamer(streamer.id)}
                  />
                ))}
              </View>
            ))}
          </View>
        )}
      </View>

      <View style={styles.actions}>
        <Button onPress={saveMutation.isPending ? () => undefined : handleSubmit}>
          {saveMutation.isPending ? 'Saving...' : 'Save settings'}
        </Button>
      </View>
    </View>
  );
};
